import SmartTooltip from '@Report/SmartTooltip';
import {
  Menu,
  MenuItem,
  MenuList,
  Text,
  MenuProps,
  TonicProps,
  MenuToggle,
  MenuToggleIcon,
  Scrollbar,
  Button,
  useColorMode,
  useColorStyle,
  SearchInput,
  Flex,
} from '@tonic-one/react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @description T maybe the key's enum
 */
interface DropdownProps<T>
  extends Omit<MenuProps, 'onChange' | 'value' | 'children'> {
  options: T[];
  //key map to value
  renderOption?: (option: T) => ReactNode;
  placeholder?: string;
  //current value
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  placeholderAsValue?: boolean;
  menuHeight?: TonicProps['height'];
  menuWidth?: TonicProps['width'];
  onSearch?: (search: string) => void;
}

//reference: https://github.com/trendmicro-frontend/tonic-ui/blob/master/packages/react-docs/components/Dropdown.js
//TODO: add ref and memo
const Dropdown = <T extends string>(props: DropdownProps<T>) => {
  const {
    options,
    renderOption = (option: T) => option,
    placeholder = '',
    value: valueProp,
    onChange: onChangeProp,
    placeholderAsValue = false,
    menuWidth,
    menuHeight,
    disabled,
    onSearch,
    ...tonicProps
  } = props;
  const { t } = useTranslation();
  const [value, setValue] = useState(valueProp ?? '');
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  useEffect(() => {
    const isControlled = valueProp !== undefined;
    if (isControlled) {
      setValue(valueProp);
    }
  }, [valueProp]);

  const onChange = useCallback(
    (nextValue: T) => {
      const isControlled = valueProp !== undefined;
      if (!isControlled) {
        setValue(nextValue);
      }
      onChangeProp(nextValue);
    },
    [valueProp, onChangeProp]
  );

  const handleClickMenuItemBy = (value: T) => () => {
    onChange(value);
  };

  return (
    <Menu onClose={() => onSearch?.('')} {...tonicProps}>
      <MenuToggle>
        {({ getMenuToggleProps }) => {
          return (
            <Button
              {...getMenuToggleProps()}
              width="100%"
              variant="secondary"
              disabled={disabled}
              _hover={{
                borderColor: colorStyle.color.info,
                color: colorStyle.color.primary,
              }}
              _focusHover={{
                borderColor: colorStyle.color.info,
                color: colorStyle.color.primary,
              }}
            >
              {placeholder && !placeholderAsValue && (
                <Text opacity="0.6">{placeholder}:&nbsp;</Text>
              )}
              {placeholder && value === '' && placeholderAsValue ? (
                <SmartTooltip
                  label={placeholder}
                  contentStyle={{
                    opacity: 0.6,
                    height: 'auto',
                  }}
                >
                  {placeholder}
                </SmartTooltip>
              ) : (
                <SmartTooltip
                  label={renderOption(value)}
                  contentStyle={{
                    height: 'auto',
                  }}
                >
                  {renderOption(value)}
                </SmartTooltip>
              )}
              <MenuToggleIcon ml="auto" />
            </Button>
          );
        }}
      </MenuToggle>
      <MenuList
        width={menuWidth ?? '100%'}
        maxHeight={menuHeight}
        overflow="auto"
        paddingY="0px"
        position="relative"
      >
        {onSearch && (
          <Flex
            position="sticky"
            top={0}
            px="2x"
            py="2x"
            background={
              colorMode === 'dark'
                ? colorStyle.background.tertiary
                : colorStyle.background.primary
            }
            zIndex={1}
          >
            <SearchInput
              placeholder={t('log.button.search')}
              onChange={(e) => onSearch(e.target.value)}
              onClearInput={() => onSearch('')}
              flex={1}
            />
          </Flex>
        )}
        <Scrollbar>
          {options.length === 0 && (
            <Text
              textAlign="center"
              pt="2x"
              pb="4x"
              color={colorStyle.color.secondary}
            >
              {t('common.noData')}
            </Text>
          )}
          {options.map((option) => (
            <MenuItem
              key={option}
              onClick={handleClickMenuItemBy(option)}
              wordBreak="break-word"
            >
              {renderOption(option)}
            </MenuItem>
          ))}
        </Scrollbar>
      </MenuList>
    </Menu>
  );
};

export default Dropdown;
